import useHttpQuery from '@actions/services/firestore/browser/useHttpQuery';
import type {
    UseGetTemplateByIdParams,
    UseGetTemplateByIdResult,
    GetTemplateByIdResponse,
} from './types';

/**
 * Hook for fetching a single template by ID from the API
 *
 * Fetches a template with its metadata and optionally a specific version.
 * If no version is specified, returns the latest version.
 * Uses useHttpQuery for efficient caching and error handling.
 * Authentication is automatically handled by useHttpQuery.
 *
 * @param params - Configuration for the hook
 * @param params.templateId - The unique identifier of the template to fetch
 * @param params.versionId - Optional version ID to fetch a specific version
 * @param params.enabled - Whether the query should be enabled (default: true)
 * @returns Object containing template data, version data, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * const { template, version, isLoadingTemplate, error } = useGetTemplateById({
 *   templateId: 'template-123',
 *   versionId: 'v1.0.0' // optional
 * });
 *
 * if (isLoadingTemplate) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!template) return <div>Template not found</div>;
 *
 * return (
 *   <div>
 *     <h1>{template.name}</h1>
 *     <p>{template.description}</p>
 *     {version && <p>Version: {version.version}</p>}
 *   </div>
 * );
 * ```
 */
const useGetTemplateById = ({
    templateId,
    versionId,
    enabled = true,
}: UseGetTemplateByIdParams): UseGetTemplateByIdResult => {
    const queryParams = new URLSearchParams();
    if (versionId) {
        queryParams.append('versionId', versionId);
    }

    const queryString = queryParams.toString();
    const pathname = `/templates/${templateId}${queryString ? `?${queryString}` : ''}`;

    const { data, isLoading, error, refetch } = useHttpQuery<GetTemplateByIdResponse>({
        pathname,
        method: 'GET',
        key: `template-${templateId}${versionId ? `-${versionId}` : ''}`,
        queryOptions: { enabled },
    });

    return {
        template: data?.template || null,
        version: data?.version || null,
        versions: data?.versions || [],
        isLoadingTemplate: isLoading,
        error: error || undefined,
        refetch,
    };
};

export default useGetTemplateById;
